// CommonMark 의 emphasis flanking 규칙은 CJK 글자가 ASCII 구두점과 인접한 `**` 주변에서
// 종료 조건 (Rule 6) 을 만족 못 시켜 한국어 / 일본어 본문에서 `**bold**조사` 패턴이 raw 텍스트로
// 노출된다 (예: `**주문건수(▲27.6%)**는` → 별표 그대로 보임). 깨지는 케이스만 surgical 하게
// `<strong>...</strong>` 로 치환 — rehype-raw + rehype-sanitize 가 그대로 통과시키므로 native
// bold 와 동일하게 렌더된다.
//
// 코드 펜스 (``` ... ```) 와 인라인 코드 (`...`) 안의 `**` 는 보호한다 — markdown-table-normalize
// 와 동일한 sentinel 패턴.

const PROTECTED = /```[\s\S]*?```|`[^`\n]*`/g;
const SENTINEL_OPEN = '​__CJK_PROT_';
const SENTINEL_CLOSE = '_PROT_CJK__​';
const SENTINEL_PATTERN = /​__CJK_PROT_(\d+)_PROT_CJK__​/g;

// Hangul Jamo / Hiragana / Katakana / Hangul Compatibility Jamo / CJK Ext A / CJK Unified /
// Hangul Syllables. 더 많은 범위 (Ext B-G 등) 가 필요해지면 여기에 추가.
const CJK =
  '\\u1100-\\u11FF\\u3040-\\u30FF\\u3130-\\u318F\\u3400-\\u4DBF\\u4E00-\\u9FFF\\uAC00-\\uD7AF';

// `**...**` 가 CJK 글자 뒤에 바로 붙은 경우 (opening 깨짐) 또는 CJK 글자 앞에 바로 붙은 경우
// (closing 깨짐). 내용 안에 줄바꿈/별표 없음 — 멀티라인 / 중첩 bold 는 의도적으로 미지원
// (드문 케이스 + 정규식 안정성).
// 본문 문자 클래스에서 파이프 `|` 도 제외한다 — 표 셀 경계를 넘지 못하게 막아, CJK 로 끝나는
// bold 셀이 한 행에 둘 이상일 때 (`| **영업1팀** | x | **목표달성** |`) 첫 셀의 닫는 `**` 를
// 여는 마커로 오인해 셀을 가로질러 묶는 cross-cell 오매칭을 방지한다.
const PRECEDED_BY_CJK = new RegExp(`(?<=[${CJK}])\\*\\*([^*\\n|]+?)\\*\\*`, 'g');
const FOLLOWED_BY_CJK = new RegExp(`\\*\\*([^*\\n|]+?)\\*\\*(?=[${CJK}])`, 'g');

export function normalizeCjkEmphasis(input: string): string {
  if (!input || input.indexOf('**') < 0) return input;

  const protectedBlocks: string[] = [];
  const stashed = input.replace(PROTECTED, (m) => {
    const idx = protectedBlocks.length;
    protectedBlocks.push(m);
    return `${SENTINEL_OPEN}${idx}${SENTINEL_CLOSE}`;
  });

  const transformed = stashed
    .replace(PRECEDED_BY_CJK, (_match, inner) => `<strong>${inner}</strong>`)
    .replace(FOLLOWED_BY_CJK, (_match, inner) => `<strong>${inner}</strong>`);

  return transformed.replace(SENTINEL_PATTERN, (_match, idx) => protectedBlocks[Number(idx)] ?? '');
}
